/**
 * Unified analytics + error monitoring boot.
 * Reads from env (safe to ship placeholders; no-ops if keys absent).
 *
 * Set these in /app/frontend/.env for production:
 *   REACT_APP_GA_ID          — e.g. G-ABC1234567      (Google Analytics 4)
 *   REACT_APP_SENTRY_DSN     — e.g. https://abc@...   (Error tracking)
 *   REACT_APP_POSTHOG_KEY    — e.g. phc_abc...        (Product analytics + session replay)
 *   REACT_APP_POSTHOG_HOST   — e.g. https://us.i.posthog.com
 */

import * as Sentry from '@sentry/react';
import ReactGA from 'react-ga4';
import posthog from 'posthog-js';

const GA_ID = process.env.REACT_APP_GA_ID;
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const APP_ENV = process.env.NODE_ENV === 'production' ? 'production' : 'development';

// ——— Google Analytics 4 ———
export function initAnalytics() {
    if (!GA_ID) {
        if (APP_ENV === 'production') {
            // eslint-disable-next-line no-console
            console.info('[analytics] GA disabled — REACT_APP_GA_ID not set');
        }
        return;
    }
    try {
        ReactGA.initialize(GA_ID, {
            gaOptions: { anonymizeIp: true }, // GDPR friendly
        });
        ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.hash });
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[analytics] GA init failed:', e?.message);
    }
}

export function trackPageview(path) {
    if (GA_ID) {
        try { ReactGA.send({ hitType: 'pageview', page: path }); } catch (e) { /* silent */ }
    }
    if (POSTHOG_KEY) {
        try { posthog.capture('$pageview', { $current_url: path }); } catch (e) { /* silent */ }
    }
}

export function trackEvent(category, action, label, value) {
    // GA4 event (via react-ga4)
    if (GA_ID) {
        try { ReactGA.event({ category, action, label, value }); } catch (e) { /* silent */ }
    }
    // PostHog event
    if (POSTHOG_KEY) {
        try {
            posthog.capture(action || category, {
                category, label, value,
            });
        } catch (e) { /* silent */ }
    }
    // Meta Pixel — Custom Event mirror so every trackEvent shows up in Events Manager too.
    // This lets you target audiences on ANY product action (e.g. "people who completed quiz").
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        try {
            window.fbq('trackCustom', action || category, {
                category, label, value,
            });
        } catch (e) { /* silent */ }
    }
}

/**
 * Fire a STANDARD conversion event across Meta Pixel + GA4 + Google Ads.
 *
 *   trackConversion('Lead')              — phone OTP / email signup completed
 *   trackConversion('CompleteRegistration') — full profile / quiz finished
 *   trackConversion('Schedule')          — counseling session booked
 *   trackConversion('Purchase', { value: 2500 }) — paid counseling
 *
 * Why this is separate from trackEvent:
 *   Meta + Google Ads optimize on STANDARD event names ('Lead', 'Purchase').
 *   Sending a custom name to Meta/Ads = the algorithm doesn't know how to
 *   optimize. Standard names unlock conversion bidding.
 *
 * Each platform gets only what it understands:
 *   • Meta Pixel: standard `track(name, params)` signature
 *   • GA4:        gtag('event', name, params) — recorded as a Conversion
 *   • Google Ads: gtag('event', 'conversion', { send_to: 'AW-.../LABEL' })
 *                 — but we don't know the per-event conversion LABEL yet,
 *                 so we ship a default mapping that the founder can refine
 *                 once they create conversion actions in Google Ads UI.
 */
const GOOGLE_ADS_ID = 'AW-3545894117';
// Map standard event names → Google Ads conversion labels.
// These labels are EMPTY for now — once the founder creates conversion
// actions in Google Ads (Tools → Conversions), they'll fill these in.
const GOOGLE_ADS_CONVERSION_LABELS = {
    Lead: '',                  // e.g. 'AbCdEfGh1234'
    CompleteRegistration: '',
    Schedule: '',
    Purchase: '',
};

export function trackConversion(eventName, params = {}) {
    // 1. Meta Pixel — standard event (powers Meta Ads conversion optimization)
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        try { window.fbq('track', eventName, params); } catch (e) { /* silent */ }
    }

    // 2. GA4 — record as a generic event (mark as Conversion inside GA4 UI)
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        try { window.gtag('event', eventName, params); } catch (e) { /* silent */ }
    }

    // 3. Google Ads — only fires if a label has been provided for this event.
    //    Until labels are populated, this no-ops safely.
    const label = GOOGLE_ADS_CONVERSION_LABELS[eventName];
    if (label && typeof window !== 'undefined' && typeof window.gtag === 'function') {
        try {
            window.gtag('event', 'conversion', {
                send_to: `${GOOGLE_ADS_ID}/${label}`,
                ...params,
            });
        } catch (e) { /* silent */ }
    }

    // 4. PostHog — also keep a copy so product analytics see the same conversions
    if (POSTHOG_KEY) {
        try { posthog.capture(`conversion_${eventName.toLowerCase()}`, params); }
        catch (e) { /* silent */ }
    }
}

// ——— PostHog — product analytics + session replay ———
export function initPostHog() {
    if (!POSTHOG_KEY) {
        if (APP_ENV === 'production') {
            // eslint-disable-next-line no-console
            console.info('[posthog] disabled — REACT_APP_POSTHOG_KEY not set');
        }
        return;
    }
    try {
        posthog.init(POSTHOG_KEY, {
            api_host: POSTHOG_HOST,
            // SPA-aware pageview tracking (works with HashRouter + BrowserRouter)
            capture_pageview: 'history_change',
            // Autocapture clicks, forms, submits (no manual wiring)
            autocapture: true,
            // Session replay — the killer feature.
            // Privacy: mask all text & inputs by default so no student PII leaks.
            session_recording: {
                maskAllInputs: true,
                maskTextSelector: '*',
                recordCrossOriginIframes: false,
            },
            // Capture unhandled errors (de-duped against Sentry on the backend)
            capture_exceptions: false, // Sentry owns errors; keep PostHog lean
            // Respect DNT headers
            respect_dnt: true,
            // Don't record sessions in development
            disable_session_recording: APP_ENV !== 'production',
            persistence: 'localStorage+cookie',
            loaded: (ph) => {
                // Expose to window only in non-prod for debugging
                if (APP_ENV !== 'production' && typeof window !== 'undefined') {
                    window.posthog = ph;
                }
            },
        });
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[posthog] init failed:', e?.message);
    }
}

/**
 * Associate future events with a logged-in user.
 * Call this from UserContext after supabase session is hydrated.
 */
export function identifyUser({ id, email, role, fullName }) {
    if (!id) return;
    if (POSTHOG_KEY) {
        try {
            posthog.identify(id, {
                email: email || undefined,
                name: fullName || undefined,
                role: role || 'student',
            });
        } catch (e) { /* silent */ }
    }
    if (SENTRY_DSN) {
        try {
            Sentry.setUser({ id, email, username: fullName });
        } catch (e) { /* silent */ }
    }
}

/**
 * Clear user association on logout. Prevents anonymous events from being
 * mis-attributed to the previous user.
 */
export function resetUser() {
    if (POSTHOG_KEY) {
        try { posthog.reset(); } catch (e) { /* silent */ }
    }
    if (SENTRY_DSN) {
        try { Sentry.setUser(null); } catch (e) { /* silent */ }
    }
}

// ——— Sentry error monitoring ———
export function initSentry() {
    if (!SENTRY_DSN) {
        if (APP_ENV === 'production') {
            // eslint-disable-next-line no-console
            console.info('[sentry] disabled — REACT_APP_SENTRY_DSN not set');
        }
        return;
    }
    try {
        Sentry.init({
            dsn: SENTRY_DSN,
            environment: APP_ENV,
            integrations: [Sentry.browserTracingIntegration()],
            tracesSampleRate: APP_ENV === 'production' ? 0.1 : 1.0, // 10% in prod
            replaysSessionSampleRate: 0.0, // disabled (PostHog owns replay)
            replaysOnErrorSampleRate: 0.0,
            // Ignore noisy errors
            ignoreErrors: [
                'ResizeObserver loop limit exceeded',
                'Non-Error promise rejection captured',
                'Network request failed',
            ],
        });
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[sentry] init failed:', e?.message);
    }
}

export { Sentry, posthog };
